/* [EZ] Raw-HTML behaviours for the scraped ez-page-* marketing pages.
 *
 * The page scraper keeps markup + CSS and strips every <script>, so every interaction on a scraped
 * page arrives dead: the compliance badges do not open, accordions do not toggle, carousel arrows do
 * nothing, the video modal never fills. This is the page-side twin of ez-switcher.js, which solved the
 * same problem for PDPs — revive the behaviour from the CLASSES the markup already carries rather than
 * importing the source site's JS.
 *
 * Why not just ship the source's scripts: they need jQuery (which arrives inside a 322KB
 * tools.min.js), plus a 40KB bundle that mixes this page's accordion/tab/modal logic with the site's
 * navigation, plus swiper.js — ~365KB of third-party code, a jQuery global, and nav code hunting for
 * elements that do not exist in our DOM. Everything below is native and dependency-free.
 *
 * Covers, per page: badge reveal + accordion + arrow tracks + tab groups + video modal (wi-fi,
 * trust-center), Swiper repair + pagination + the data-fancybox video lightbox (ifa2026).
 *
 * ONE file for every scraped page. Each behaviour is independent, feature-detected off its own
 * markup, and a no-op when that markup is absent — so the same file serves trust-center-v2, wi-fi and
 * ifa2026 without per-page branching.
 *
 * EVERYTHING IS SCOPED TO `.ez-rawhtml`. The scraped bodies reuse generic class names (.accordion,
 * .modal, .swiper-slide) that also exist in Horizon, so an unscoped listener would hijack real theme
 * UI. Every query below starts from an .ez-rawhtml root.
 *
 * Idempotent: each element is marked once via a data flag, so a second run (theme editor re-render,
 * section reload) will not double-bind.
 */
(function () {
  'use strict';

  var ROOT_SEL = '.ez-rawhtml';
  var DONE = 'ezPageBound';

  function roots() { return Array.prototype.slice.call(document.querySelectorAll(ROOT_SEL)); }
  function all(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  // A per-PURPOSE flag, not one flag per element. Two behaviours legitimately bind the same node —
  // bindTabs and bindSeriesFilter both match [role="tab"] — and with a single shared key whichever ran
  // first silently consumed the other. The series filter looked correctly wired and never bound, so
  // every tab showed every card.
  function once(el, purpose) {
    var k = purpose ? DONE + purpose : DONE;
    if (el.dataset[k]) return false;
    el.dataset[k] = '1';
    return true;
  }

  // ---------------------------------------------------------------- 1. badge / card reveal
  // trust-center-v2: the source script gave each .compliance-badge tabindex=0 + role=button and
  // toggled `is-active` to reveal the certification's type + description. We render through headless
  // Chrome so that copy IS in the DOM (11 badges x type + detail), but it stays collapsed without the
  // toggle. Opening one closes the others, matching the source behaviour.
  function bindReveal(root) {
    var groups = {};
    all(root, '.compliance-badge').forEach(function (el) {
      var key = 'compliance-badge';
      (groups[key] = groups[key] || []).push(el);
    });
    Object.keys(groups).forEach(function (key) {
      var items = groups[key];
      items.forEach(function (el) {
        if (!once(el)) return;
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
        if (!el.hasAttribute('role')) el.setAttribute('role', 'button');
        function toggle() {
          var open = el.classList.contains('is-active');
          items.forEach(function (o) { if (o !== el) o.classList.remove('is-active'); });
          el.classList.toggle('is-active', !open);
          el.setAttribute('aria-expanded', String(!open));
        }
        el.addEventListener('click', toggle);
        el.addEventListener('keydown', function (e) {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          toggle();
        });
      });
    });
  }

  // ---------------------------------------------------------------- 2. accordion
  // wi-fi FAQ: .accordion > .accordion-item[.open] > button.accordion-header[aria-expanded] +
  // .accordion-panel. The markup is already accessible, so only the toggle is missing.
  function bindAccordion(root) {
    all(root, '.accordion').forEach(function (acc) {
      all(acc, '.accordion-header').forEach(function (hdr) {
        if (!once(hdr)) return;
        hdr.addEventListener('click', function () {
          var item = hdr.closest('.accordion-item');
          if (!item) return;
          var open = item.classList.contains('open');
          // single-open, which is how the source rendered it (one .accordion-item.open at load)
          all(acc, '.accordion-item').forEach(function (o) {
            if (o === item) return;
            o.classList.remove('open');
            var h = o.querySelector('.accordion-header');
            if (h) h.setAttribute('aria-expanded', 'false');
          });
          item.classList.toggle('open', !open);
          hdr.setAttribute('aria-expanded', String(!open));
        });
      });
    });
  }

  // ---------------------------------------------------------------- 3. arrow-driven tracks
  // Two shapes on wi-fi, same contract: a viewport that scrolls a track of cards, driven by a
  // prev/next pair whose `disabled` state reflects position.
  //   .battery-family-arrow--prev/--next  ->  .battery-family-viewport / .battery-family-track
  //   .new-4g-tab-arrow--prev/--next      ->  #new-4g-product-grid  (aria-controls names it)
  // Scrolling the viewport (rather than transforming the track) keeps the CSS untouched and works
  // whether the source used translate or overflow.
  var TRACKS = [
    { prev: '.battery-family-arrow--prev', next: '.battery-family-arrow--next', viewport: '.battery-family-viewport', item: '.battery-family-card' },
    { prev: '.new-4g-tab-arrow--prev', next: '.new-4g-tab-arrow--next', viewport: '#new-4g-product-grid', item: '.new-4g-tab-card' }
  ];
  function bindTracks(root) {
    TRACKS.forEach(function (t) {
      var viewport = root.querySelector(t.viewport);
      var prev = root.querySelector(t.prev);
      var next = root.querySelector(t.next);
      if (!viewport || (!prev && !next)) return;
      var items = all(viewport, t.item);
      if (!items.length) return;

      // A track frozen mid-scroll by the headless render would start off-position; reset it.
      var wrapper = viewport.querySelector('.battery-family-track') || viewport;
      if (wrapper.style.transform) wrapper.style.transform = '';

      function step() {
        var first = items[0];
        var second = items[1];
        if (second) {
          var d = second.getBoundingClientRect().left - first.getBoundingClientRect().left;
          if (d > 1) return d;
        }
        return first.getBoundingClientRect().width || viewport.clientWidth;
      }
      function sync() {
        var max = viewport.scrollWidth - viewport.clientWidth - 1;
        if (prev) prev.disabled = viewport.scrollLeft <= 1;
        if (next) next.disabled = viewport.scrollLeft >= max;
      }
      function go(dir) {
        viewport.scrollBy({ left: dir * step(), behavior: 'smooth' });
        // scrollBy is async; settle the arrow state after the smooth scroll lands
        window.setTimeout(sync, 400);
      }
      if (prev && once(prev)) prev.addEventListener('click', function () { go(-1); });
      if (next && once(next)) next.addEventListener('click', function () { go(1); });
      if (once(viewport)) viewport.addEventListener('scroll', sync, { passive: true });
      sync();
    });
  }

  // ---------------------------------------------------------------- 4. tab groups
  // Generic on role=tab + aria-controls, so it covers .new-4g-tab-button and any similar group.
  // NOTE the limit: the source rebuilt the panel's contents from a JS data array on every tab change.
  // That data is not in the DOM, so only the tabs whose panel markup was captured can show content —
  // see the note in docs/page-scraper/video-urls.json's sibling issue list. This restores the active
  // state and any panel that IS present; it cannot invent products that were never scraped.
  function bindTabs(root) {
    var tabs = all(root, '[role="tab"]');
    if (!tabs.length) return;
    var groups = {};
    tabs.forEach(function (t) {
      var key = t.getAttribute('aria-controls') || 'default';
      (groups[key] = groups[key] || []).push(t);
    });
    Object.keys(groups).forEach(function (key) {
      var group = groups[key];
      var activeCls = null;
      group.some(function (t) {
        var hit = Array.prototype.find.call(t.classList, function (c) { return /is-active|active|selected/.test(c); });
        if (hit) activeCls = hit;
        return !!hit;
      });
      group.forEach(function (tab) {
        if (!once(tab)) return;
        tab.addEventListener('click', function () {
          group.forEach(function (o) {
            var on = o === tab;
            o.setAttribute('aria-selected', String(on));
            if (activeCls) o.classList.toggle(activeCls, on);
          });
          var panel = key && key !== 'default' ? root.querySelector('#' + key) : null;
          if (panel) panel.setAttribute('aria-labelledby', tab.id || '');
          // Panels that exist as siblings (one per tab) get shown/hidden by index.
          var idx = tab.dataset.index;
          if (idx == null || !panel) return;
          var panes = all(panel, '[data-tab-index]');
          if (!panes.length) return;
          panes.forEach(function (p) { p.hidden = p.dataset.tabIndex !== String(idx); });
        });
      });
    });
  }

  // ---------------------------------------------------------------- 5. video modal
  // wi-fi: <img class="mobile-video-play"> opens #videoModal and sets #videoFrame's src (which ships
  // empty). The source read the url from a data attribute on the trigger; where that is absent we fall
  // back to the first <video> on the page that already has a src, so the modal is never empty-black.
  function bindVideoModal(root) {
    var modal = root.querySelector('#videoModal, .modal[role="dialog"]');
    var frame = root.querySelector('#videoFrame');
    if (!modal || !frame) return;

    function close() {
      modal.style.display = 'none';
      try { frame.pause(); } catch (e) { /* not yet playable */ }
      frame.removeAttribute('src');
      frame.load();
    }
    function open(src) {
      if (!src) return;
      frame.setAttribute('src', src);
      modal.style.display = 'block';
      var p = frame.play();
      if (p && p.catch) p.catch(function () { /* autoplay blocked — controls are present */ });
    }

    all(root, '.mobile-video-play, [data-video-src]').forEach(function (trigger) {
      if (!once(trigger)) return;
      trigger.style.cursor = 'pointer';
      trigger.addEventListener('click', function () {
        var src = trigger.getAttribute('data-video-src');
        if (!src) {
          var inline = root.querySelector('video[src]');
          if (inline) src = inline.getAttribute('src');
        }
        open(src);
      });
    });

    if (once(modal)) {
      modal.addEventListener('click', function (e) {
        if (e.target === modal || (e.target.classList && e.target.classList.contains('modal-close'))) close();
      });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
    }
  }

  // ---------------------------------------------------------------- 6. swiper markup
  // ifa2026 ships POST-INITIALISATION Swiper DOM, because we scrape through a real browser: cloned
  // `swiper-slide-duplicate` slides, `swiper-container-initialized`, and — the visible bug — inline
  // `transform: translate3d(-4263px, ...)` frozen at whatever offset the carousel happened to be at.
  // So these carousels do not merely fail to advance, they start scrolled into blank space.
  //
  // Clearing the inline transform puts every wrapper back to slide 1 and makes prev/next work by
  // scrolling the container. This is a repair, not a reimplementation of Swiper: loop mode, autoplay
  // and pagination-driven jumps are not restored (see the header note in ez-page-raw.js's owner doc).
  // ifa2026 renders `.swiper-pagination` as a SIBLING of the container, not a child, so scoping the
  // bullet lookup to the container found nothing: no bullet was clickable and the active dot never
  // moved. Widen the same way the prev/next lookup already does, and guard against a parent that
  // holds two carousels by preferring the container's own bullets when it has any.
  // Every carousel here is a Swiper — the markup is Swiper's, and so is most of the captured CSS. The
  // hand-rolled repair that used to live here could clear a frozen transform and step slides, but it
  // could never reproduce slidesPerView, centeredSlides, parallax or loop, so ifa's carousels showed
  // sliced slides and would not advance properly.
  //
  // The source runs Swiper 4.5.0, which is what this markup was generated by (.swiper-container,
  // swiper-slide-duplicate, swiper-container-horizontal). So load that exact version and hand it the
  // source's OWN configs, read out of the page's script rather than guessed:
  //   .mySwiper1              speed 1000, spaceBetween 1, parallax, loop, autoplay, arrow nav
  //                           (no slidesPerView -> 1 full-width slide, which is the intended layout)
  //   #certify .swiper-container  slidesPerView 3, centeredSlides, loop, speed 800, autoplay
  //   .mySwiper1a             vertical, spaceBetween 30, loop, autoplay, speed 1000
  //
  // Swiper is fetched from the theme's own assets, next to this file — never a CDN, so the page has no
  // third-party runtime dependency — and only on a page that actually has carousel markup.
  // Resolve a carousel's pager as a NODE, not a selector string. ifa renders .swiper-pagination as a
  // SIBLING of the container on some bands and a child on others, and there are three pagers on the
  // page — a global selector like the source's `.swiper-pagination` would hand the same element to two
  // different carousels. Look inside first, then in the parent, and only when that parent holds a
  // single carousel.
  function pagerElFor(el) {
    var own = el.querySelector('.swiper-pagination');
    if (own) return own;
    var p = el.parentElement;
    if (!p) return null;
    if (p.querySelectorAll('.swiper-container, .swiper').length > 1) return null;
    return p.querySelector('.swiper-pagination');
  }

  var SWIPER_CONFIGS = [
    {
      sel: '.mySwiper1',
      opts: function () {
        return {
          speed: 1000, spaceBetween: 1, parallax: true, loop: true, autoplay: true,
          navigation: { nextEl: '.sa-a3v1-c1 .swiper-button-next1', prevEl: '.sa-a3v1-c1 .swiper-button-prev1' },
          breakpoints: { 768: { spaceBetween: 10 } },
          on: {
            // the category labels are the source's read-out of the current slide
            slideChangeTransitionStart: function () {
              var labels = document.querySelectorAll('.sa-a3v1-d1 li');
              for (var i = 0; i < labels.length; i++) labels[i].classList.toggle('on', i === this.realIndex);
            },
          },
        };
      },
      // The source leaves those labels read-only. Clicking one is the obvious thing to try, so wire it.
      after: function (sw) {
        var labels = document.querySelectorAll('.sa-a3v1-d1 li');
        Array.prototype.forEach.call(labels, function (li, i) {
          if (!once(li)) return;
          li.style.cursor = 'pointer';
          li.addEventListener('click', function () { sw.slideToLoop(i); });
        });
      },
    },
    {
      sel: '#certify .swiper-container',
      opts: function (el) {
        var pager = pagerElFor(el);
        var o = {
          slidesPerView: 3, centeredSlides: true, loop: true, speed: 800, autoplay: true,
          breakpoints: { 768: { slidesPerView: 1 } },
        };
        if (pager) o.pagination = { el: pager, clickable: true };
        return o;
      },
    },
    {
      sel: '.mySwiper1a',
      opts: function (el) {
        var n = el.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)').length;
        var pager = pagerElFor(el);
        var o = {
          spaceBetween: 30, direction: 'vertical', speed: 1000,
          loop: n > 1, autoplay: n > 1,
        };
        if (pager) o.pagination = { el: pager, clickable: true };
        return o;
      },
    },
  ];

  // Scraped through a real browser, the markup is Swiper's POST-INITIALISATION DOM: cloned slides,
  // an `initialized` class, a frozen inline transform, and per-slide inline width/height Swiper had
  // computed for the old viewport. Re-initialising on top of that clones the clones and pins slides to
  // stale pixel widths — which is exactly why band 01 rendered sliced instead of one full image. So
  // put the DOM back to the state Swiper expects to be handed.
  function resetSwiperDom(el) {
    Array.prototype.forEach.call(el.querySelectorAll('.swiper-slide-duplicate'), function (d) { d.remove(); });
    el.classList.remove('swiper-container-initialized', 'swiper-container-horizontal', 'swiper-container-vertical');
    var wrap = el.querySelector('.swiper-wrapper');
    if (wrap) { wrap.style.transform = ''; wrap.style.transitionDuration = ''; wrap.removeAttribute('id'); }
    Array.prototype.forEach.call(el.querySelectorAll('.swiper-slide'), function (sl) {
      sl.style.width = ''; sl.style.height = ''; sl.style.marginRight = ''; sl.style.marginBottom = '';
      sl.classList.remove('swiper-slide-active', 'swiper-slide-prev', 'swiper-slide-next');
    });
    // pagination bullets are regenerated by Swiper; leaving the scraped ones doubles them up
    var pager = pagerElFor(el);
    if (pager) pager.innerHTML = '';
  }

  var swiperLoading = false;
  function withSwiper(cb) {
    if (window.Swiper) return cb();
    if (swiperLoading) return void document.addEventListener('ez:swiper-ready', cb, { once: true });
    swiperLoading = true;
    var self = document.querySelector('script[src*="ez-page-raw"]');
    if (!self) return;                                   // cannot locate the assets folder — do nothing
    var tag = document.createElement('script');
    tag.src = self.src.replace(/ez-page-raw\.js/, 'ez-swiper.js');
    tag.onload = function () { document.dispatchEvent(new Event('ez:swiper-ready')); cb(); };
    document.head.appendChild(tag);
  }

  function bindSwiper(root) {
    var found = SWIPER_CONFIGS.filter(function (c) { return root.querySelector(c.sel); });
    if (!found.length) return;                           // no carousels on this page — load nothing
    withSwiper(function () {
      found.forEach(function (c) {
        Array.prototype.forEach.call(root.querySelectorAll(c.sel), function (el) {
          if (!once(el)) return;
          resetSwiperDom(el);
          try {
            var sw = new window.Swiper(el, c.opts(el));
            if (c.after) c.after(sw);
          } catch (e) { if (window.console) console.warn('[ez-page] swiper init failed for ' + c.sel, e); }
        });
      });
    });
  }

  // ------------------------------------------------------------------ 6b. marquee rows (sa-a3v8)
  // The two photo strips under People & Moments. The source drives them with SuperSlide:
  //   $('.sa-a3v8 .asht1').slide({effect:'leftMarquee', interTime:35, ...})
  //   $('.sa-a3v8 .asht2').slide({effect:'leftMarquee', opp:true, ...})
  // `opp:true` reverses it, so the top strip travels right-to-left and the bottom one left-to-right.
  //
  // THE SIZING TRAP. Like Swiper, SuperSlide writes its measurements as INLINE styles, and the scrape
  // captured them from a NARROW headless viewport. Inline beats the stylesheet, so the page shipped:
  //
  //   li        style="float: left; width: 140px"      CSS wants 21.0625rem = 337px
  //   .tempWrap style="width: 924px"                   should be 100%
  //   ul        style="width: 4620px; left: -581px"    frozen mid-scroll
  //
  // which is why the tiles rendered at roughly 40% of the reference. Stripping those inline values
  // hands sizing back to the CSS. SuperSlide also left 14 of its own <li class="clone"> in the markup,
  // so cloning the run without removing them first duplicates the duplicates.
  function resetSuperSlideDom(box) {
    var wrap = box.querySelector('.tempWrap');
    if (wrap) { wrap.style.width = '100%'; wrap.style.overflow = 'hidden'; wrap.style.position = 'relative'; }
    var track = box.querySelector('.tempWrap ul') || box.querySelector('.bd ul') || box.querySelector('ul');
    if (!track) return null;
    Array.prototype.forEach.call(track.querySelectorAll('li.clone'), function (c) { c.remove(); });
    ['width', 'left', 'position', 'overflow'].forEach(function (p) { track.style.removeProperty(p); });
    Array.prototype.forEach.call(track.children, function (li) {
      li.style.removeProperty('width');
      li.style.removeProperty('float');
    });
    return track;
  }

  function bindMarquee(root) {
    [['.sa-a3v8 .asht1', -1], ['.sa-a3v8 .asht2', 1]].forEach(function (pair) {
      var box = root.querySelector(pair[0]);
      if (!box || !once(box)) return;
      var track = resetSuperSlideDom(box);
      if (!track || !track.children.length) return;

      track.style.display = 'flex';
      track.style.width = 'max-content';
      track.style.willChange = 'transform';
      // a flex item defaults to flex-shrink:1, which would squeeze the CSS width straight back out
      Array.prototype.forEach.call(track.children, function (li) { li.style.flex = '0 0 auto'; });

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      var originals = Array.prototype.slice.call(track.children);
      originals.forEach(function (li) {
        var c = li.cloneNode(true);
        c.style.flex = '0 0 auto';
        c.setAttribute('aria-hidden', 'true');   // a duplicated run should not be read out twice
        track.appendChild(c);
      });

      var dir = pair[1];
      var half = 0;
      var x = 0;
      var paused = false;
      box.addEventListener('mouseenter', function () { paused = true; });
      box.addEventListener('mouseleave', function () { paused = false; });

      (function step() {
        if (!half) {
          // measured after layout; until the images have widths this stays 0 and we simply wait
          half = track.scrollWidth / 2;
          if (half) x = dir < 0 ? 0 : -half;
        }
        if (half && !paused && !document.hidden) {
          x += dir * 0.5;
          if (x <= -half) x += half;
          if (x >= 0) x -= half;
          track.style.transform = 'translate3d(' + x + 'px,0,0)';
        }
        requestAnimationFrame(step);
      })();
    });
  }
  // ---------------------------------------------------------------- 7. hotspots (Explore On-Site)
  // ifa's "Explore EZVIZ Smart Home On-Site" floorplan: pulsing blue dots over a room render, each
  // revealing a product panel. The dots animate on their own (CSS keyframes boderM/boderM1 are in the
  // stylesheet), which is why they LOOK alive — but clicking one did nothing, so five of the six
  // panels were unreachable. The panels are switched by inline display, and the active dot by .on.
  //
  // Note the markup has SIX dots and FIVE panels, so bind only dots that have a matching panel rather
  // than assuming they pair up.
  function bindHotspots(root) {
    all(root, '.sa-a3v2c1').forEach(function (box) {
      var dots = all(box, '.sa-a3v2c1d1 > li');
      var panels = all(box, '.sa-a3v2c1d2 > li');
      if (!dots.length || !panels.length) return;
      dots.forEach(function (dot, n) {
        var panel = panels[n];
        if (!panel || !once(dot)) return;
        dot.style.cursor = 'pointer';
        dot.addEventListener('click', function () {
          dots.forEach(function (d) { d.classList.remove('on'); });
          dot.classList.add('on');
          panels.forEach(function (p) { p.style.display = 'none'; });
          panel.style.display = 'list-item';
        });
      });
    });
  }

  // ---------------------------------------------------------------- 8. 3D rotator (People & Moments)
  // Six figures on a hexagonal cylinder. My first pass just cleared the inline styles and let the
  // stylesheet's `carouselRotateY 60s linear infinite` spin it — which moved, but at a fixed crawl and
  // deaf to the wheel. The source does not use that animation at all; it kills it and drives the
  // rotation itself, with scroll momentum:
  //
  //   gsap.set(carousel, { animation: 'none', transform: 'translate(-50%,-50%) rotateY(0deg)' });
  //   wheel:  delta = clamp(e.deltaY, -120, 120);  speed += delta * 0.006;  speed = clamp(speed, -4, 4)
  //   frame:  rotateY += speed;  speed *= 0.96;  if (|speed| < 0.08) speed = sign(speed) * 0.08
  //
  // Reproduced exactly, minus GSAP — a plain rAF ticker does the same job. So it idles at 0.08°/frame,
  // a scroll injects momentum that decays back to the idle speed, and scrolling the other way reverses
  // the direction. That is where 'faster when I scroll' and the up/down reversal both come from.
  var rotatorPhysics = { speed: 0.08, bound: false };

  function bindRotator(root) {
    var cars = all(root, '.xzmh-conxa .carousel').filter(once);
    if (!cars.length) return;

    cars.forEach(function (el) {
      // the headless render baked GSAP's mid-frame inline style on; clear it so we start from zero
      el.style.removeProperty('translate');
      el.style.removeProperty('rotate');
      el.style.removeProperty('scale');
      el.style.animation = 'none';
      el.style.transform = 'translate(-50%, -50%) rotateY(0deg)';
    });

    if (rotatorPhysics.bound) return;
    rotatorPhysics.bound = true;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // the source listens on window, not the element — the cylinder responds to page scroll anywhere
    window.addEventListener('wheel', function (e) {
      var delta = Math.max(Math.min(e.deltaY, 120), -120);
      rotatorPhysics.speed += delta * 0.006;
      rotatorPhysics.speed = Math.max(Math.min(rotatorPhysics.speed, 4), -4);
    }, { passive: true });

    var rotateY = 0;
    (function tick() {
      if (!document.hidden) {
        rotateY += rotatorPhysics.speed;
        rotatorPhysics.speed *= 0.96;
        if (Math.abs(rotatorPhysics.speed) < 0.08) rotatorPhysics.speed = rotatorPhysics.speed < 0 ? -0.08 : 0.08;
        var t = 'translate(-50%, -50%) rotateY(' + rotateY + 'deg)';
        cars.forEach(function (el) { el.style.transform = t; });
      }
      requestAnimationFrame(tick);
    })();
  }

  // ---------------------------------------------------------------- 9. in-page nav (navto -> #askN)
  // The sticky section nav is <a navto="ask5"> — an attribute, not an href — so with the scripts
  // stripped none of the eight links did anything at all.
  function bindNavTo(root) {
    all(root, '[navto]').forEach(function (link) {
      if (!once(link)) return;
      link.style.cursor = 'pointer';
      link.addEventListener('click', function (e) {
        var t = document.getElementById(link.getAttribute('navto'));
        if (!t) return;
        e.preventDefault();
        t.scrollIntoView({ behavior: 'smooth', block: 'start' });
        var nav = link.closest('ul');
        if (nav) all(nav, 'li').forEach(function (li) { li.classList.remove('on'); });
        if (link.parentElement) link.parentElement.classList.add('on');
      });
    });
  }
  // ------------------------------------------------------- 10. product-grid series filter (wi-fi)
  // "Find Your Best One" has four tabs — All / Single-Lens / Dual-Lens / Triple-Lens — and they all
  // point at the SAME panel. The source filtered by rebuilding the grid from a JS data array, so with
  // the scripts stripped the tabs could never filter anything: no card in the scraped DOM carries a
  // category at all.
  //
  // The cards now carry data-series (added when the grid was aligned to the products we actually
  // stock), so filter on that. A tab whose series has no cards is hidden rather than left to show an
  // empty grid — which also means the strip repairs itself as products land: add a card with the
  // right data-series and its tab comes back on its own.
  var SERIES = { 'new-4g-tab-all': null, 'new-4g-tab-single': 'single', 'new-4g-tab-dual': 'dual', 'new-4g-tab-triple': 'triple' };

  function bindSeriesFilter(root) {
    all(root, '#new-4g-product-grid').forEach(function (grid) {
      var cards = all(grid, '.new-4g-tab-card');
      if (!cards.length) return;
      var strip = root.querySelector('[role="tablist"]')
        || (grid.closest('.new-4g-tab-panel') && grid.closest('.new-4g-tab-panel').parentElement);
      var tabs = all(strip || root, '.new-4g-tab-button');
      if (!tabs.length) return;

      function apply(series) {
        cards.forEach(function (c) {
          var mine = !series || c.getAttribute('data-series') === series;
          // NOT the hidden attribute on its own: `[hidden]{display:none}` is specificity 0,1,0 and the
          // captured stylesheet sets `.new-4g-tab-card{display:flex}` later in the cascade, which wins.
          // Filtering looked wired and did nothing — every tab showed every card. An inline style beats
          // both. The attribute is kept alongside it for assistive tech.
          c.hidden = !mine;
          c.style.display = mine ? '' : 'none';
        });
      }

      tabs.forEach(function (tab) {
        var series = SERIES[tab.id];
        // hide a tab that can never match anything — but never the All tab
        if (series && !cards.some(function (c) { return c.getAttribute('data-series') === series; })) {
          tab.hidden = true;
          return;
        }
        if (!once(tab, 'Series')) return;
        tab.addEventListener('click', function () {
          tabs.forEach(function (t) {
            t.classList.toggle('new-4g-tab-is-active', t === tab);
            t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
            t.tabIndex = t === tab ? 0 : -1;
          });
          apply(series);
        });
      });

      apply(null);
    });
  }

  // ------------------------------------------------------------- 11. comparison swap (wi-fi)
  // "Find Your Best One" is a spec column with a product dropdown. Changing it did nothing, because
  // the source rebuilt the column from a JS data array on every change and the scraper only ever sees
  // the ONE product that happened to be rendered. So the other option's values do not exist in the
  // page at all — no amount of DOM wiring recovers them.
  //
  // They are supplied instead by an inert <script type="application/json" class="ez-compare-data">
  // next to the table, keyed by <option value>, sourced from our own catalogue. Only the ALTERNATE
  // product is embedded: the initially-selected one is snapshotted from the DOM below, so nothing is
  // duplicated and the page diverges from the scrape as little as possible.
  //
  // A row count that does not match the rendered column is left alone rather than padded — a spec list
  // silently gaining or losing a line is worse than one that does not switch.
  function bindCompare(root) {
    all(root, '#comparisonTable').forEach(function (table) {
      var payloadEl = table.querySelector('.ez-compare-data');
      var columns = all(table, '.product-column');
      if (!columns.length || !payloadEl) return;

      var data;
      try { data = JSON.parse(payloadEl.textContent); } catch (e) { return; }

      // Snapshot every column as it stands, keyed by the option it currently shows. With two columns
      // that means both products come from the DOM and the embedded payload is only a fallback — the
      // page is its own source of truth, and a spec edited in the markup cannot drift from the swap.
      columns.forEach(function (col) {
        var sel = col.querySelector('select.g-dropdown-select');
        if (!sel) return;
        var lines = all(col, '.g-find-best-line');
        var nameEl = col.querySelector('.product-name');
        var descEl = col.querySelector('.product-description');
        var imgEl = col.querySelector('img.product-image');
        data[sel.value] = {
          name: nameEl ? nameEl.textContent.trim() : '',
          description: descEl ? descEl.textContent.trim() : '',
          image: imgEl ? imgEl.getAttribute('src') : '',
          lines: lines.map(function (ln) { return all(ln, 'li').map(function (li) { return li.innerHTML; }); }),
        };
      });

      columns.forEach(function (col) {
        var sel = col.querySelector('select.g-dropdown-select');
        if (!sel || !once(sel)) return;
        var nameEl = col.querySelector('.product-name');
        var descEl = col.querySelector('.product-description');
        var imgEl = col.querySelector('img.product-image');
        var lines = all(col, '.g-find-best-line');

        sel.addEventListener('change', function () {
          var d = data[sel.value];
          if (!d) return;
          if (nameEl && d.name) nameEl.textContent = d.name;
          if (descEl && d.description) descEl.textContent = d.description;
          if (imgEl && d.image) { imgEl.setAttribute('src', d.image); imgEl.setAttribute('alt', d.name || ''); }
          (d.lines || []).forEach(function (vals, i) {
            var ln = lines[i];
            if (!ln) return;
            var items = all(ln, 'li');
            if (items.length !== vals.length) return;   // shape mismatch — leave the line untouched
            items.forEach(function (li, n) { li.innerHTML = vals[n]; });
          });

          // Two columns showing the same product compares nothing. Push the other one to whatever this
          // column just stopped showing, which is what a two-slot comparator is for.
          columns.forEach(function (other) {
            if (other === col) return;
            var os = other.querySelector('select.g-dropdown-select');
            if (!os || os.value !== sel.value) return;
            var alt = Array.prototype.map.call(os.options, function (o) { return o.value; })
              .filter(function (v) { return v !== sel.value; })[0];
            if (alt === undefined) return;
            os.value = alt;
            os.dispatchEvent(new Event('change'));
          });
        });
      });
    });
  }
  // ---------------------------------------------------------------- 12. lightbox (data-fancybox)
  // ifa2026's video gallery is six <a href="….mp4" data-fancybox> tiles. On the source, Fancybox
  // intercepts the click and plays the film in an overlay. With the scripts stripped the anchor does
  // what an anchor does — navigates away to a bare .mp4 — which loses the page. That is the single
  // most visible break left on ifa, and no other behaviour here covers it.
  //
  // Not a Fancybox reimplementation: one overlay, the clicked item, Esc / backdrop / button to close.
  // No gallery paging, thumbnails or captions — nothing in the scraped markup asks for them.
  // data-width/data-height on the anchor give the intended aspect, so the frame does not jump.
  var lb = null;
  function lightboxEl() {
    if (lb) return lb;
    lb = document.createElement('div');
    lb.className = 'ez-lightbox';
    lb.setAttribute('hidden', '');
    lb.innerHTML =
      '<div class="ez-lightbox__backdrop"></div>' +
      '<div class="ez-lightbox__frame" role="dialog" aria-modal="true" aria-label="Media viewer">' +
        '<button type="button" class="ez-lightbox__close" aria-label="Close">&times;</button>' +
        '<div class="ez-lightbox__body"></div>' +
      '</div>';
    var css = document.createElement('style');
    // Inline so the lightbox never depends on a per-page stylesheet, and high z-index because the
    // scraped bodies stack their own layers well above the theme's.
    css.textContent =
      '.ez-lightbox{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center}' +
      '.ez-lightbox[hidden]{display:none}' +
      '.ez-lightbox__backdrop{position:absolute;inset:0;background:rgba(0,0,0,.9)}' +
      '.ez-lightbox__frame{position:relative;width:min(92vw,1280px);max-height:88vh;display:flex;flex-direction:column}' +
      '.ez-lightbox__body{position:relative;width:100%;background:#000;display:flex;align-items:center;justify-content:center}' +
      '.ez-lightbox__body video,.ez-lightbox__body img{display:block;width:100%;height:auto;max-height:88vh}' +
      '.ez-lightbox__close{position:absolute;top:-2.4rem;right:0;width:2rem;height:2rem;line-height:1;' +
        'font-size:1.9rem;color:#fff;background:none;border:0;cursor:pointer;padding:0}';
    document.head.appendChild(css);
    document.body.appendChild(lb);
    lb.querySelector('.ez-lightbox__backdrop').addEventListener('click', closeLightbox);
    lb.querySelector('.ez-lightbox__close').addEventListener('click', closeLightbox);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeLightbox(); });
    return lb;
  }

  function closeLightbox() {
    if (!lb || lb.hasAttribute('hidden')) return;
    // Drop the source so the file stops downloading the moment the overlay closes — these are 60MB.
    lb.querySelector('.ez-lightbox__body').textContent = '';
    lb.setAttribute('hidden', '');
    document.documentElement.style.overflow = '';
  }

  function openLightbox(href, w, h) {
    var el = lightboxEl();
    var body = el.querySelector('.ez-lightbox__body');
    body.textContent = '';
    if (w && h) body.style.aspectRatio = w + ' / ' + h;
    var isVideo = /\.(mp4|webm|mov|m4v)(\?|$)/i.test(href);
    var media = document.createElement(isVideo ? 'video' : 'img');
    if (isVideo) {
      media.controls = true; media.autoplay = true; media.playsInline = true;
      media.setAttribute('playsinline', '');
    } else {
      media.alt = '';
    }
    media.src = href;
    body.appendChild(media);
    el.removeAttribute('hidden');
    document.documentElement.style.overflow = 'hidden';
  }

  function bindLightbox(root) {
    all(root, 'a[data-fancybox]').forEach(function (a) {
      if (!once(a)) return;
      a.addEventListener('click', function (e) {
        var href = a.getAttribute('href');
        if (!href || href.charAt(0) === '#') return;   // not a media link — leave it alone
        e.preventDefault();
        openLightbox(href, a.getAttribute('data-width'), a.getAttribute('data-height'));
      });
    });
  }

  // ------------------------------------------------------------------ 13. scroll reveal (data-in-view)
  // trust-center-v2 animates most of its content in on scroll. The source marks each element
  // data-in-view="0" and parks it 100px low and transparent, then flips it to data-in-view="1" at
  // translate3d(0,0,0) once it enters the viewport:
  //
  //   <p data-in-view="0" style="opacity:0; transform: translate3d(0px, 100px, 0px) …">
  //
  // The scraper strips that script, so all 44 of them stayed parked — every heading and badge caption
  // sitting 100px below its own card, on top of the one beneath. That is the 'texts are disarray' and
  // 'text is on the under card' report, and it is a LAYOUT break, not an animation nicety: the copy is
  // in the wrong place whether or not anything animates.
  //
  // Worse in our copy than on the source: the scrape lost the `opacity: 0`, so ours were displaced AND
  // visible, where the source at least hides them until revealed.
  //
  // Reproduce the reveal with an IntersectionObserver — same end state, no dependency. Anything
  // already in view on load reveals immediately, so nothing depends on the reader scrolling. Without
  // IntersectionObserver, or under prefers-reduced-motion, reveal everything at once: being visible in
  // the right place always beats being animated.
  function revealEl(el) {
    el.setAttribute('data-in-view', '1');
    el.style.opacity = '1';
    el.style.transform = 'translate3d(0px, 0px, 0px) scale3d(1, 1, 1) rotateX(0deg)';
  }

  function bindInView(root) {
    var els = all(root, '[data-in-view="0"]').filter(once);
    if (!els.length) return;

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || typeof IntersectionObserver === 'undefined') {
      els.forEach(function (el) { el.style.transition = 'none'; revealEl(el); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        revealEl(e.target);
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.01 });

    els.forEach(function (el) { io.observe(el); });

    // Safety net: if anything is still parked a moment after load — an observer that never fired, a
    // hidden ancestor, a tab restored mid-scroll — show it rather than leave copy in the wrong place.
    setTimeout(function () {
      els.forEach(function (el) {
        if (el.getAttribute('data-in-view') === '0') revealEl(el);
      });
    }, 3000);
  }
  // ---------------------------------------------------------------- init
  function init() {
    roots().forEach(function (root) {
      bindReveal(root);
      bindAccordion(root);
      bindTracks(root);
      bindTabs(root);
      bindVideoModal(root);
      bindSwiper(root);
      bindMarquee(root);
      bindLightbox(root);
      bindHotspots(root);
      bindRotator(root);
      bindNavTo(root);
      bindSeriesFilter(root);
      bindCompare(root);
      bindInView(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  // The theme editor swaps section markup without a page load, so re-bind on its events. The DONE
  // flag keeps this from double-binding anything that survived.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', init);
})();
